import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Row, Col } from "reactstrap";
import {
  getSolicitudes,
  resetSolicitudFlag,
  getFarmacias,
  exportarReporteSolicitudes,
} from "../../../slices/thunks";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import TiempoProcesamientoWidgetCompact from "../../../Components/Common/TiempoProcesamientoWidgetCompact";
import { DatosSesion } from "../../../Components/Common/DatosSesion";
import { getProveedoresDashboard } from "../../../helpers/backend_helper";
import SkeletonListaSolicitudes from "../../../Components/Common/SkeletonListaSolicitudes";
import MercantilBuscarRegistrarModal from "../../../Components/Common/MercantilBuscarRegistrarModal";
import { useDebounce } from "../../../hooks/useDebounce";

const ListaSolicitudes = (props) => {
  const dispatch = useDispatch();
  const selectLayoutState = (state) => state;

  // Estado para la búsqueda y filtros
  const [tipoSolicitud, setTipoSolicitud] = useState("");
  const [estatus, setEstatus] = useState("");
  const [aseguradora, setAseguradora] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [farmaciaActual, setFarmaciaActual] = useState(null);

  // Estado para filtro de proveedor (solo admin)
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState("");

  // Estado para filtro de estado geográfico
  const [estadoGeografico, setEstadoGeografico] = useState("");

  // Estado para filtro de calificación (con/sin/1-5)
  const [calificacionFiltro, setCalificacionFiltro] = useState("");

  // Estado para filtro de efectividad de entrega
  const [efectividadFiltro, setEfectividadFiltro] = useState("");

  // Estado para controlar si hay resultados
  const [noResults, setNoResults] = useState(false);

  // Estado para ordenamiento
  const [sortField, setSortField] = useState("facturacion.fecha");
  const [sortOrder, setSortOrder] = useState("asc"); // asc = más antigua primero

  // Estado para búsqueda
  const [searchQuery, setSearchQuery] = useState("");

  // Hook de debounce para búsqueda
  const debouncedSearchQuery = useDebounce(searchQuery, 2000);

  // Agregar estados para la paginación
  const [pageSize, setPageSize] = useState(20);
  const [pageIndex, setPageIndex] = useState(0);

  // Estado para modal de búsqueda Mercantil (Buscar y Registrar Orden)
  const [showMercantilBuscarModal, setShowMercantilBuscarModal] = useState(false);
  // Trigger para forzar recarga del listado tras registrar una orden manualmente
  const [reloadCounter, setReloadCounter] = useState(0);

  // Definir las opciones de los filtros
  const tiposSolicitud = [
    { value: "Aguda", label: "Aguda" },
    { value: "Cronica", label: "Crónica" },
  ];

  // Array dinámico de estatus basado en la farmacia actual
  const estatusSolicitud = useMemo(() => {
    const baseEstatus = [
      { value: "Nueva Solicitud", label: "Nueva Solicitud" },
      { value: "En Transito", label: "En Tránsito" },
      { value: "Entregado", label: "Entregado" },
      { value: "Cancelado", label: "Cancelado" },
      { value: "Por Renovar", label: "Por Renovar" },
      { value: "Por Reprogramar", label: "Por Reprogramar" },
    ];

    // Solo agregar "Virtual" si la farmacia actual es 00082
    if (farmaciaActual?.codigo_myh === "00082" || farmaciaActual?.codigo_myh === "00073") {
      baseEstatus.push({ value: "Virtual", label: "Virtual" });
    }

    return baseEstatus;
  }, [farmaciaActual]);

  // Estados de Venezuela para el filtro geográfico
  const estadosVenezuela = [
    { value: "Amazonas", label: "Amazonas" },
    { value: "Anzoategui", label: "Anzoátegui" },
    { value: "Apure", label: "Apure" },
    { value: "Aragua", label: "Aragua" },
    { value: "Barinas", label: "Barinas" },
    { value: "Bolivar", label: "Bolívar" },
    { value: "Carabobo", label: "Carabobo" },
    { value: "Cojedes", label: "Cojedes" },
    { value: "Delta Amacuro", label: "Delta Amacuro" },
    { value: "Distrito Capital", label: "Distrito Capital" },
    { value: "Falcon", label: "Falcón" },
    { value: "Guarico", label: "Guárico" },
    { value: "La Guaira", label: "La Guaira" },
    { value: "Lara", label: "Lara" },
    { value: "Merida", label: "Mérida" },
    { value: "Miranda", label: "Miranda" },
    { value: "Monagas", label: "Monagas" },
    { value: "Nueva Esparta", label: "Nueva Esparta" },
    { value: "Portuguesa", label: "Portuguesa" },
    { value: "Sucre", label: "Sucre" },
    { value: "Tachira", label: "Táchira" },
    { value: "Trujillo", label: "Trujillo" },
    { value: "Yaracuy", label: "Yaracuy" },
    { value: "Zulia", label: "Zulia" },
  ];

  const pageData = createSelector(selectLayoutState, (state) => ({
    solicitudes: state.Solicitudes.solicitudes,
    error: state.Solicitudes.error,
    loading: state.Solicitudes.loading,
    errorMsg: state.Solicitudes.errorMsg,
    colorMsg: state.Solicitudes.colorMsg,
    exito: state.Solicitudes.exito,
  }));

  const farmaciasPageData = createSelector(selectLayoutState, (state) => ({
    farmacias: state.Farmacias.farmacias,
    loadingFarmacias: state.Farmacias.loading,
  }));

  const { solicitudes, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);

  const { farmacias, loadingFarmacias } = useSelector(farmaciasPageData);

  // Obtener datos de sesión
  const perfil = DatosSesion("perfil");
  const farmaciaId = DatosSesion("farmacia");
  const esMyHealth = perfil === "MyHealth";

  // Para usuarios MyHealth: forzar y bloquear el filtro de aseguradora en "MyH+"
  useEffect(() => {
    if (esMyHealth && aseguradora !== "MyH+") {
      setAseguradora("MyH+");
    }
  }, [esMyHealth, aseguradora]);

  // Calcular estadísticas de solicitudes desde el backend
  const estadisticas = useMemo(() => {
    // solicitudes[2] contiene las estadísticas globales del backend
    if (!solicitudes || !solicitudes[2]) {
      return {
        nuevas: 0,
        enTransito: 0,
        porReprogramar: 0,
        total: 0,
      };
    }

    const stats = solicitudes[2];
    const nuevas = stats["Nueva Solicitud"] || 0;
    const enTransito = stats["En Transito"] || 0;
    const porReprogramar = stats["Por Reprogramar"] || 0;

    return {
      nuevas,
      enTransito,
      porReprogramar,
      total: nuevas + enTransito + porReprogramar, // Suma de las tres cards
    };
  }, [solicitudes]);

  // Efecto para cargar las farmacias
  useEffect(() => {
    dispatch(getFarmacias());
  }, [dispatch]);

  // Efecto para establecer la farmacia actual cuando se tengan los datos
  useEffect(() => {
    if (farmaciaId && farmacias && farmacias[0]) {
      const farmaciaEncontrada = farmacias[0].find(
        (farmacia) => farmacia.id === farmaciaId
      );
      setFarmaciaActual(farmaciaEncontrada);
    }
  }, [farmacias, farmaciaId]);

  // Efecto para cargar las solicitudes iniciales
  useEffect(() => {
    // Solo ejecutar una vez cuando se carga el componente
    const cargarSolicitudesIniciales = () => {
      // Verificar si tenemos la información necesaria
      if (!farmaciaActual && !(perfil === "Administrador" || perfil === "MyHealth")) {
        return;
      }

      setIsLoading(true);

      // Construir query con filtros por defecto
      const queryParams = [];

      // Agregar búsqueda si existe
      const hasBusqueda = debouncedSearchQuery && debouncedSearchQuery.trim();
      if (hasBusqueda) {
        queryParams.push(`query=${encodeURIComponent(debouncedSearchQuery.trim())}`);
      }

      // Si hay búsqueda, NO aplicar filtro de estatus (búsqueda global)
      // Si NO hay búsqueda, aplicar filtro de estatus
      if (!hasBusqueda) {
        // Si hay estatus seleccionado, usarlo
        if (estatus) {
          queryParams.push(`estatus=${estatus}`);
        } else {
          // Si no hay estatus guardado ni seleccionado, usar "Nueva Solicitud" por defecto
          queryParams.push(`estatus=Nueva Solicitud`);
        }

        // Agregar tipo si está seleccionado (solo cuando NO hay búsqueda)
        if (tipoSolicitud) {
          queryParams.push(`tipo=${tipoSolicitud}`);
        }
      }

      // Agregar código de proveedor según el perfil
      if (perfil === "Administrador" || perfil === "MyHealth") {
        // Admin: usar proveedor seleccionado si existe
        if (proveedorSeleccionado) {
          queryParams.push(`codProveedor=${proveedorSeleccionado}`);
        }
      } else if (farmaciaActual) {
        // Farmacia: siempre usar su código asignado
        queryParams.push(`codProveedor=${farmaciaActual.codigo_myh}`);
      }

      // Agregar filtro de estado geográfico si está seleccionado
      if (estadoGeografico) {
        queryParams.push(`estadoGeografico=${encodeURIComponent(estadoGeografico)}`);
      }

      // Agregar filtro de aseguradora si está seleccionado
      if (aseguradora) {
        queryParams.push(`aseguradora=${encodeURIComponent(aseguradora)}`);
      }

      // Agregar filtro de calificación si está seleccionado
      if (calificacionFiltro) {
        queryParams.push(`calificacion=${encodeURIComponent(calificacionFiltro)}`);
      }

      // Agregar filtro de efectividad si está seleccionado
      if (efectividadFiltro) {
        queryParams.push(`efectividad=${encodeURIComponent(efectividadFiltro)}`);
      }

      // Agregar parámetros de ordenamiento
      queryParams.push(`sort_field=${sortField}`);
      queryParams.push(`sort_order=${sortOrder}`);

      // Agregar parámetros de paginación
      queryParams.push(`limit=${pageSize}`);
      queryParams.push(`offset=${pageIndex * pageSize}`);

      const finalQuery = queryParams.join("&");

      // Llamada directa a la API sin pasar por applyFilters
      dispatch(getSolicitudes(finalQuery))
        .finally(() => setIsLoading(false));
    };

    // Ejecutar la carga inicial después de un pequeño retraso para asegurar que
    // todos los estados estén correctamente inicializados
    const timer = setTimeout(cargarSolicitudesIniciales, 100);

    // Limpiar el timer si el componente se desmonta
    return () => clearTimeout(timer);
  }, [dispatch, farmaciaActual, perfil, estatus, tipoSolicitud, sortField, sortOrder, debouncedSearchQuery, pageIndex, pageSize, proveedorSeleccionado, estadoGeografico, aseguradora, calificacionFiltro, efectividadFiltro, reloadCounter]);

  // Efecto para resetear paginación cuando cambian filtros o búsqueda
  useEffect(() => {
    // Resetear a página 1 cuando cambia la búsqueda, estatus, tipo o proveedor
    setPageIndex(0);
  }, [debouncedSearchQuery, estatus, tipoSolicitud, proveedorSeleccionado, estadoGeografico, aseguradora, calificacionFiltro, efectividadFiltro]);

  // Estado para proveedores del endpoint del dashboard (solo admin)
  const [proveedoresDashboard, setProveedoresDashboard] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);

  // Efecto para cargar proveedores desde el endpoint del dashboard (solo admin)
  useEffect(() => {
    if (perfil === "Administrador" || perfil === "MyHealth") {
      setCargandoProveedores(true);
      getProveedoresDashboard()
        .then((response) => {
          if (response.data?.data) {
            setProveedoresDashboard(response.data.data);
          }
          setCargandoProveedores(false);
        })
        .catch((err) => {
          console.error("Error al cargar proveedores:", err);
          setCargandoProveedores(false);
        });
    }
  }, [perfil]);


  // Referencia para el debounce timer
  const searchTimerRef = useRef(null);

  // Función para exportar a Excel con los filtros actuales
  const handleExportarExcel = useCallback(async () => {
    try {
      setIsExporting(true);

      // Determinar el código de farmacia según el perfil
      let codigoFarmacia = "";
      if (perfil === "Administrador" || perfil === "MyHealth") {
        // Admin: usar proveedor seleccionado si existe
        codigoFarmacia = proveedorSeleccionado || "";
      } else {
        // Farmacia: usar su código asignado
        codigoFarmacia = farmaciaActual?.codigo_myh || "";
      }

      // Construir parámetros basados en filtros actuales
      const params = {
        tipo: tipoSolicitud || "Todas",
        estatus: estatus || "",
        farmacia: codigoFarmacia,
        // Incluir búsqueda si existe
        query: searchQuery || "",
        // Incluir estado geográfico si existe
        estadoGeografico: estadoGeografico || "",
        // Incluir aseguradora si existe
        aseguradora: aseguradora || "",
        // Si hay búsqueda, no incluir fechas (exportar todo lo que coincida)
        fecha_inicio: "",
        fecha_fin: "",
      };

      await dispatch(exportarReporteSolicitudes(params)).unwrap();
      toast.success("Reporte exportado exitosamente");
    } catch (error) {
      console.error("Error al exportar:", error);
      toast.error(error.message || "Error al exportar el reporte");
    } finally {
      setIsExporting(false);
    }
  }, [tipoSolicitud, estatus, farmaciaActual, perfil, dispatch, proveedorSeleccionado, searchQuery, estadoGeografico, aseguradora]);

  // Función helper para agregar parámetros de ordenamiento
  const addSortParams = useCallback((queryParams) => {
    queryParams.push(`sort_field=${sortField}`);
    queryParams.push(`sort_order=${sortOrder}`);
    return queryParams;
  }, [sortField, sortOrder]);

  // Handler para cambiar ordenamiento al hacer clic en columna
  const handleSort = useCallback((field) => {
    // Si es el mismo campo, cambiar dirección
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Si es un campo diferente, establecer nuevo campo y orden descendente
      setSortField(field);
      setSortOrder("desc");
    }
    // El useEffect se encargará de recargar los datos
  }, [sortField, sortOrder]);

  // Componente para header ordenable
  const SortableHeader = ({ label, field }) => (
    <div
      className="d-flex align-items-center cursor-pointer user-select-none"
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer' }}
    >
      <span>{label}</span>
      {sortField === field && (
        <i className={`ms-1 ${sortOrder === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
      )}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        header: () => <SortableHeader label="ID" field="identificador" />,
        cell: (cell) => {
          return <span className="fw-semibold">{cell.getValue()}</span>;
        },
        accessorKey: "identificador",
        enableColumnFilter: false,
      },
      /*       {
        header: "Detalle",
        accessorKey: "detalle",
        enableColumnFilter: false,
      }, */

      {
        header: () => <SortableHeader label="Tipo" field="tipo" />,
        accessorKey: "tipo",
        enableColumnFilter: false,
        cell: (cell) => {
          const tipo = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!tipo) {
            return <span>-</span>;
          }

          let badgeClass = "";
          let colorClass = "";

          switch (tipo) {
            case "Aguda":
              badgeClass = "Aguda";
              colorClass = "bg-danger-subtle text-danger";
              break;
            case "Cronica":
              badgeClass = "Crónica";
              colorClass = "bg-success-subtle text-success";
              break;
            default:
              colorClass = "bg-info-subtle text-info";
          }

          return (
            <span className={`badge ${colorClass}`}>
              {badgeClass || tipo}
            </span>
          );
        },
      },

      {
        header: "Aseguradora",
        accessorKey: "aseguradora",
        enableColumnFilter: false,
        cell: (cell) => {
          const valor = cell.getValue() || "MyH+";
          let colorClass;
          if (valor === "Seguros Mercantil") {
            colorClass = "bg-primary-subtle text-primary";
          } else if (valor === "Latina Salud") {
            colorClass = "bg-success-subtle text-success";
          } else if (valor?.toLowerCase() === "hispana de seguros") {
            colorClass = "bg-info-subtle text-info";
          } else if (valor?.toLowerCase() === "real seguros") {
            colorClass = "bg-danger-subtle text-danger";
          } else {
            colorClass = "bg-warning-subtle text-warning";
          }
          return <span className={`badge ${colorClass}`}>{valor}</span>;
        },
      },

      {
        header: "Solicitante",
        accessorKey: "solicitante",
        enableColumnFilter: false,
        cell: (cell) => {
          const nombre = cell.getValue();
          const cedula = cell.row.original.solicitanteCedula;
          return (
            <div>
              <div className="fw-medium">{nombre}</div>
              {cedula && (
                <div className="text-muted mb-0" style={{ fontSize: '12px' }}>
                  {cedula}
                </div>
              )}
            </div>
          );
        },
      },

      {
        header: () => <SortableHeader label="Facturación" field="facturacion.fecha" />,
        id: "facturacion",
        accessorFn: (row) => {
          const facturacion = row.facturacion;
          if (!facturacion || !Array.isArray(facturacion) || facturacion.length === 0) {
            return null;
          }
          const ultima = facturacion[facturacion.length - 1];
          return ultima?.fecha || null;
        },
        enableColumnFilter: false,
        cell: (cell) => {
          const fechaIso = cell.getValue();
          if (!fechaIso) {
            return <span>-</span>;
          }

          const fecha = new Date(fechaIso);
          return fecha.toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        },
      },
      /* {
        header: "Cod. Farmacia",
        accessorKey: "codigoProveedor",
        enableColumnFilter: false,
        cell: (cell) => {
          const codigoProveedor = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!codigoProveedor) {
            return <span>-</span>;
          }
          
          let badgeClass = "";
          let colorClass = "";
      
          switch (codigoProveedor) {
            case "00077":
              badgeClass = "Caracas";
              colorClass = "bg-primary-subtle text-primary";
              break;
            case "00082":
              badgeClass = "Mérida";
              colorClass = "bg-danger-subtle text-danger";
              break;
            case "00083":
              badgeClass = "Carabobo";
              colorClass = "bg-warning-subtle text-info";
              break;
            default:
              badgeClass = "Lara";
              colorClass = "bg-warning-subtle text-warning";
          }
      
          return (
            <span className={`badge ${colorClass}`}>
              {codigoProveedor} - {badgeClass}
            </span>
          );
        },
      }, */
      {
        header: "Farmacia",
        accessorKey: "farmaciaAsignada",
        enableColumnFilter: false,
        cell: (cell) => {
          const farmacia = cell.getValue();
          if (!farmacia) {
            return <span>-</span>;
          }
          return <span>{farmacia}</span>;
        },
      },
      {
        header: "Estado",
        id: "estado",
        accessorFn: (row) => {
          const direccion = row.direccion;
          if (!direccion) return "";

          // Extraer solo el estado de la dirección completa
          const estadoMatch = direccion.match(/Estado\s+([^,]+)(?:,|$)/);
          if (!estadoMatch || !estadoMatch[1]) return "";

          // Normalizar: convertir a formato título
          return estadoMatch[1]
            .trim()
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        },
        enableColumnFilter: false,
        cell: (cell) => {
          const estado = cell.getValue();
          return <span>{estado || "-"}</span>;
        },
      },
      {
        header: "Prioridad",
        accessorKey: "prioridad",
        enableColumnFilter: false,
        cell: (cell) => {
          const prioridad = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!prioridad) {
            return <span>-</span>;
          }
          let badgeClass = "";

          switch (prioridad.toLowerCase()) {
            case "alta":
              badgeClass = "danger";
              break;
            case "media":
              badgeClass = "warning";
              break;
            case "baja":
              badgeClass = "success";
              break;
            default:
              badgeClass = "info";
          }

          return (
            <span
              className={`badge bg-${badgeClass}-subtle text-${badgeClass}`}
            >
              {prioridad}
            </span>
          );
        },
      },
      {
        header: () => <SortableHeader label="Estatus" field="estatus" />,
        accessorKey: "estatus",
        enableColumnFilter: false,
        cell: (cell) => {
          const estatus = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!estatus) {
            return <span>-</span>;
          }
          let badgeClass = "";

          switch (estatus.toLowerCase()) {
            case "entregado":
              badgeClass = "success";
              break;
            case "pendiente":
              badgeClass = "warning";
              break;
            case "cancelado":
              badgeClass = "danger";
              break;
            default:
              badgeClass = "info";
          }

          return (
            <span
              className={`badge bg-${badgeClass}-subtle text-${badgeClass}`}
            >
              {estatus}
            </span>
          );
        },
      },
      {
        header: "Calificación",
        accessorKey: "calificaciones",
        enableColumnFilter: false,
        cell: (cell) => {
          const row = cell.row.original;
          const lista = row?.calificaciones?.length > 0
            ? row.calificaciones
            : (row?.calificacion ? [row.calificacion] : []);
          if (!lista.length) {
            return <span className="text-muted">-</span>;
          }
          const valoraciones = lista
            .map((c) => c?.valoracion)
            .filter((v) => typeof v === "number" && v > 0);
          if (!valoraciones.length) {
            return <span className="text-muted">-</span>;
          }
          const promedio = valoraciones.reduce((a, b) => a + b, 0) / valoraciones.length;
          const cantidad = valoraciones.length;
          let badgeClass = "warning";
          if (promedio >= 4) badgeClass = "success";
          else if (promedio <= 2) badgeClass = "danger";
          const texto = cantidad > 1
            ? `★ ${promedio.toFixed(1)} (${cantidad})`
            : `★ ${promedio.toFixed(1)}`;
          return (
            <span
              className={`badge bg-${badgeClass}-subtle text-${badgeClass}`}
              title={`${cantidad} calificaci${cantidad === 1 ? "ón" : "ones"}`}
            >
              {texto}
            </span>
          );
        },
      },
      {
        header: "Efectividad de la Entrega",
        accessorKey: "efectividad",
        enableColumnFilter: false,
        cell: (cell) => {
          const efectividad = cell.getValue();
          return <TiempoProcesamientoWidgetCompact efectividad={efectividad} />;
        },
      },
    ],
    [sortField, sortOrder, handleSort]
  );

  const resetFilters = () => {
    setSearchQuery("");
    setTipoSolicitud("");
    setEstatus("");
    setAseguradora(esMyHealth ? "MyH+" : ""); // MyHealth queda fijo en MyH+
    setProveedorSeleccionado(""); // Limpiar proveedor seleccionado (admin)
    setEstadoGeografico(""); // Limpiar estado geográfico
    setCalificacionFiltro(""); // Limpiar filtro de calificación
    setEfectividadFiltro(""); // Limpiar filtro de efectividad
    setPageIndex(0); // Resetear paginación
    setIsLoading(true);
    // Inicializar queryParams como un array vacío
    let queryParams = [];

    // Agregar parámetros de ordenamiento
    queryParams = addSortParams(queryParams);

    // Realizar la búsqueda inicial según el perfil
    if (perfil === "Administrador" || perfil === "MyHealth") {
      // Solo agregar el filtro de "Nueva Solicitud" si no se ha seleccionado otro estatus
      if (!estatus) {
        queryParams.push(`estatus=Nueva Solicitud`);
      }
      const finalQuery =
        queryParams.length > 0
          ? `${queryParams.join("&")}`
          : "";

      dispatch(getSolicitudes(finalQuery)).finally(() => setIsLoading(false));
    } else if (farmaciaActual) {
      queryParams.push(`estatus=Nueva Solicitud`);
      queryParams.push(`codProveedor=${farmaciaActual.codigo_myh}`);
      const finalQuery = queryParams.join("&");
      dispatch(getSolicitudes(finalQuery)).finally(() => setIsLoading(false));
    }
  };

  // Y en el input:
  // En el componente TableContainer, necesitaremos pasar los controles de filtro como children
  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      {/* Cards de Estadísticas - Estilo Dashboard */}
      <Row className="mb-3">
        {/* Nueva Solicitud */}
        <Col xl={3} md={6}>
          <div
            className="card card-animate overflow-hidden"
            style={{ borderLeft: '4px solid var(--vz-success)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-8"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Nueva Solicitud
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.nuevas}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                        <i className="ri-file-add-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-success-subtle text-success">
                    <i className="ri-arrow-right-line me-1"></i>Pendientes
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* En Tránsito */}
        <Col xl={3} md={6}>
          <div
            className="card card-animate overflow-hidden"
            style={{ borderLeft: '4px solid var(--vz-info)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-6"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        En Tránsito
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.enTransito}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                        <i className="ri-truck-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-info-subtle text-info">
                    <i className="ri-arrow-right-line me-1"></i>En proceso
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* Por Reprogramar */}
        <Col xl={3} md={6}>
          <div
            className="card card-animate overflow-hidden"
            style={{ borderLeft: '4px solid var(--vz-warning)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-7"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Por Reprogramar
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.porReprogramar}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                        <i className="ri-calendar-event-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-6"></span></span>
                ) : (
                  <span className="badge bg-warning-subtle text-warning">
                    <i className="ri-alert-line me-1"></i>Requiere atención
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* Total Solicitudes */}
        <Col xl={3} md={6}>
          <div
            className="card card-animate overflow-hidden"
            style={{ borderLeft: '4px solid var(--vz-primary)' }}
          >
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1 overflow-hidden">
                  {isLoading ? (
                    <div className="placeholder-glow">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        <span className="placeholder col-8"></span>
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        <span className="placeholder col-4"></span>
                      </h4>
                    </div>
                  ) : (
                    <>
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                        Total Solicitudes
                      </p>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                        {estadisticas.total}
                      </h4>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {isLoading ? (
                    <div className="avatar-sm placeholder-glow">
                      <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                    </div>
                  ) : (
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ri-file-list-3-line"></i>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3">
                {isLoading ? (
                  <span className="placeholder-glow"><span className="placeholder col-5"></span></span>
                ) : (
                  <span className="badge bg-primary-subtle text-primary">
                    <i className="ri-checkbox-circle-line me-1"></i>Registradas
                  </span>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <SkeletonListaSolicitudes rows={pageSize} />
      ) : (
        <>
          <div className="card">
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
                  <div className="search-box">
                    <input
                      id="buscar"
                      type="text"
                      className="form-control search"
                      placeholder="Buscar por cédula, ID o factura..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchQuery.trim()) {
                          e.preventDefault();
                          setPageIndex(0);
                          // Forzar recarga inmediata
                          dispatch(getSolicitudes(`query=${encodeURIComponent(searchQuery.trim())}&limit=${pageSize}&offset=0`));
                        }
                      }}
                      autoComplete="off"
                      style={{ minWidth: "220px" }}
                    />
                    <i
                      className="ri-search-line search-icon"
                      style={{ pointerEvents: "none" }}
                    ></i>
                  </div>
                  <select
                    className="form-select"
                    value={tipoSolicitud}
                    onChange={(e) => setTipoSolicitud(e.target.value)}
                    style={{ width: "auto" }}
                    title="Filtrar por tipo de solicitud"
                  >
                    <option value="">Tipo de Solicitud</option>
                    {tiposSolicitud.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select"
                    value={estatus}
                    onChange={(e) => setEstatus(e.target.value)}
                    style={{ width: "auto" }}
                    title="Filtrar por estatus de solicitud"
                  >
                    <option value="">Estatus</option>
                    {estatusSolicitud.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {/* Filtro de proveedor - Solo visible para administradores */}
                  {(perfil === "Administrador" || perfil === "MyHealth") && (
                    <select
                      className="form-select"
                      value={proveedorSeleccionado}
                      onChange={(e) => setProveedorSeleccionado(e.target.value)}
                      style={{ width: "auto", maxWidth: "220px" }}
                      title="Filtrar por proveedor/farmacia"
                      disabled={cargandoProveedores}
                    >
                      <option value="">Todos los Proveedores</option>
                      {proveedoresDashboard.map((prov) => (
                        <option key={prov.codigo} value={prov.codigo}>
                          {prov.nombre}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Filtro de estado geográfico */}
                  <select
                    className="form-select"
                    value={estadoGeografico}
                    onChange={(e) => setEstadoGeografico(e.target.value)}
                    style={{ width: "auto" }}
                    title="Filtrar por estado de Venezuela"
                  >
                    <option value="">Estado</option>
                    {estadosVenezuela.map((estado) => (
                      <option key={estado.value} value={estado.value}>
                        {estado.label}
                      </option>
                    ))}
                  </select>
                  {/* Filtro de aseguradora - Solo visible para administradores */}
                  
                    <select
                      className="form-select"
                      value={esMyHealth ? "MyH+" : aseguradora}
                      onChange={(e) => setAseguradora(e.target.value)}
                      disabled={esMyHealth}
                      style={{ width: "auto" }}
                      title="Filtrar por aseguradora"
                    >
                      {esMyHealth ? (
                        <option value="MyH+">MyH+</option>
                      ) : (
                        <>
                          <option value="">Todas las Aseguradoras</option>
                          <option value="MyH+">MyH+</option>
                          <option value="Seguros Mercantil">Seguros Mercantil</option>
                          <option value="Latina Salud">Latina Salud</option>
                          <option value="HISPANA DE SEGUROS">Hispana de Seguros</option>
                          <option value="REAL SEGUROS">Real Seguros</option>
                        </>
                      )}
                    </select>
                  {/* Filtro de calificación */}
                  <select
                    className="form-select"
                    value={calificacionFiltro}
                    onChange={(e) => setCalificacionFiltro(e.target.value)}
                    style={{ width: "auto" }}
                    title="Filtrar por calificación de servicio"
                  >
                    <option value="">Calificación</option>
                    <option value="con">Con calificación</option>
                    <option value="sin">Sin calificación</option>
                    <option value="5">5 ★</option>
                    <option value="4">4 ★</option>
                    <option value="3">3 ★</option>
                    <option value="2">2 ★</option>
                    <option value="1">1 ★</option>
                  </select>
                  {/* Filtro de efectividad */}
                  {/* <select
                    className="form-select"
                    value={efectividadFiltro}
                    onChange={(e) => setEfectividadFiltro(e.target.value)}
                    style={{ width: "auto" }}
                    title="Filtrar por efectividad de entrega"
                  >
                    <option value="">Efectividad</option>
                    <option value="critica">Crítica (≥100%)</option>
                    <option value="atencion">Atención (75-99%)</option>
                    <option value="bien">Bien (0-74%)</option>
                  </select> */}
                </div>
                <div className="d-flex gap-2">
                  {aseguradora === "Seguros Mercantil" &&
                    (perfil === "Administrador" || perfil === "Farmacia") && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowMercantilBuscarModal(true)}
                        title="Buscar una orden en el portal Mercantil y registrarla manualmente"
                      >
                        <i className="ri-search-line me-1"></i>Buscar Orden Mercantil
                      </button>
                    )}
                  <button
                    className={`btn ${searchQuery || tipoSolicitud || estatus || proveedorSeleccionado || estadoGeografico || aseguradora || calificacionFiltro || efectividadFiltro
                        ? "btn-warning"
                        : "btn-outline-secondary"
                      }`}
                    onClick={() => {
                      if (searchTimerRef.current) {
                        clearTimeout(searchTimerRef.current);
                      }
                      setNoResults(false);
                      resetFilters();
                    }}
                    title="Limpiar todos los filtros"
                  >
                    <i className="ri-filter-off-line me-1"></i>Limpiar
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleExportarExcel}
                    disabled={isExporting || isLoading}
                    title="Exportar solicitudes a Excel"
                  >
                    {isExporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Exportando...
                      </>
                    ) : (
                      <>
                        <i className="ri-file-excel-2-line me-1"></i>Exportar
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Indicador de carga */}
              {isLoading && (
                <div className="text-center my-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p className="mt-2">Cargando solicitudes...</p>
                </div>
              )}

              {/* Indicadores de filtros activos */}
              {(searchQuery || tipoSolicitud || estatus || proveedorSeleccionado || aseguradora || calificacionFiltro || efectividadFiltro) && (
                <div className="mb-3">
                  <span className="me-2">Filtros activos:</span>
                  {searchQuery && (
                    <span className="badge bg-info me-2">
                      Búsqueda: {searchQuery}
                    </span>
                  )}
                  {tipoSolicitud && (
                    <span className="badge bg-info me-2">
                      Tipo: {tipoSolicitud}
                    </span>
                  )}
                  {estatus && (
                    <span className="badge bg-info me-2">
                      Estatus: {estatus}
                    </span>
                  )}
                  {proveedorSeleccionado && (
                    <span className="badge bg-primary me-2">
                      Proveedor: {proveedorSeleccionado}
                    </span>
                  )}
                  {aseguradora && (
                    <span className="badge bg-secondary me-2">
                      Aseguradora: {aseguradora}
                    </span>
                  )}
                  {calificacionFiltro && (
                    <span className="badge bg-warning me-2">
                      Calificación: {calificacionFiltro === "con"
                        ? "Con calificación"
                        : calificacionFiltro === "sin"
                          ? "Sin calificación"
                          : `${calificacionFiltro} ★`}
                    </span>
                  )}
                  {efectividadFiltro && (
                    <span className="badge bg-danger me-2">
                      Efectividad: {efectividadFiltro === "critica"
                        ? "Crítica ≥100%"
                        : efectividadFiltro === "atencion"
                          ? "Atención 75-99%"
                          : "Bien 0-74%"}
                    </span>
                  )}
                </div>
              )}


              {/* Tabla sin scroll vertical */}
              {!noResults && Array.isArray(solicitudes[0]) && solicitudes[0].length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle table-nowrap mb-0">
                      <thead>
                        <tr>
                          {columns.map((column, index) => (
                            <th key={index}>
                              {typeof column.header === 'function' ? column.header() : column.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {solicitudes[0].map((row) => (
                          <tr
                            key={row.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(`/solicitud/ver/${row.id}`, '_blank')}
                          >
                            {columns.map((column, colIndex) => {
                              const cellValue = column.accessorFn
                                ? column.accessorFn(row)
                                : row[column.accessorKey];

                              return (
                                <td key={colIndex}>
                                  {column.cell
                                    ? column.cell({ getValue: () => cellValue, row: { original: row } })
                                    : cellValue
                                  }
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginador estilo tickets */}
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    {/* Selector de registros por página - Izquierda */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted">Registros por página:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPageIndex(0);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    {/* Contador de registros - Centro */}
                    <div className="text-muted">
                      Mostrando {pageIndex * pageSize + 1} a {Math.min((pageIndex + 1) * pageSize, solicitudes[1] || 0)} de {solicitudes[1] || 0} registros
                    </div>

                    {/* Paginador - Derecha */}
                    <div>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => pageIndex > 0 && setPageIndex(0)}>&laquo;</button>
                        </li>
                        <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => pageIndex > 0 && setPageIndex(pageIndex - 1)}>&lt;</button>
                        </li>

                        {(() => {
                          const maxPages = Math.ceil((solicitudes[1] || 0) / pageSize);
                          const maxPagesToShow = 5;
                          let startPage = Math.max(0, pageIndex - Math.floor(maxPagesToShow / 2));
                          let endPage = Math.min(maxPages - 1, startPage + maxPagesToShow - 1);

                          if (endPage - startPage + 1 < maxPagesToShow) {
                            startPage = Math.max(0, endPage - maxPagesToShow + 1);
                          }

                          const pages = [];
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${pageIndex === i ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setPageIndex(i)}>{i + 1}</button>
                              </li>
                            );
                          }
                          return pages;
                        })()}

                        <li className={`page-item ${pageIndex === Math.ceil((solicitudes[1] || 0) / pageSize) - 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => pageIndex < Math.ceil((solicitudes[1] || 0) / pageSize) - 1 && setPageIndex(pageIndex + 1)}>&gt;</button>
                        </li>
                        <li className={`page-item ${pageIndex === Math.ceil((solicitudes[1] || 0) / pageSize) - 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => pageIndex < Math.ceil((solicitudes[1] || 0) / pageSize) - 1 && setPageIndex(Math.ceil((solicitudes[1] || 0) / pageSize) - 1)}>&raquo;</button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : (!isLoading && (noResults || !Array.isArray(solicitudes[0]) || solicitudes[0].length === 0)) && (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <i className="ri-file-search-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  </div>
                  <h4>No se encontraron solicitudes</h4>
                  <p className="text-muted">
                    {searchQuery || tipoSolicitud || estatus ?
                      "No hay solicitudes que coincidan con los filtros seleccionados." :
                      "No hay solicitudes disponibles en este momento."}
                  </p>
                  {searchQuery || tipoSolicitud || estatus ? (
                    <button
                      className="btn btn-outline-primary mt-2"
                      onClick={() => {
                        if (searchTimerRef.current) {
                          clearTimeout(searchTimerRef.current);
                        }

                        setNoResults(false);
                        resetFilters();
                      }}
                    >
                      <i className="ri-refresh-line me-1"></i> Limpiar filtros
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal: Buscar y Registrar Orden Mercantil (manual) */}
      <MercantilBuscarRegistrarModal
        isOpen={showMercantilBuscarModal}
        toggle={() => setShowMercantilBuscarModal((v) => !v)}
        onSuccess={() => setReloadCounter((c) => c + 1)}
      />
    </React.Fragment>
  );
};

export { ListaSolicitudes };
